import React, { useState, useEffect } from "react";
import {
  Shield,
  Eye,
  Lock,
  Cookie,
  Database,
  Share2,
  Clock,
  MapPin,
  CreditCard,
  UserCheck,
  AlertTriangle,
  FileText,
  ChevronRight,
  Mail,
  Search,
} from "lucide-react";
import CtaSection from "../CtaSection/CtaSection";

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState(1);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    {
      id: 1,
      title: "Introduction",
      icon: Shield,
      color: "blue",
      content:
        'This policy explains how we, BUMPPY PAYMENTS PVT LTD. (hereinafter referred to as "Bumppy"), use the personal information which you provide to us when using our service, including but not limited to our website and mobile applications. By visiting and/or ordering services on this Website, you agree to the collection, use and transfer of your information as set out in this policy.',
    },
    {
      id: 2,
      title: "Information Collection",
      icon: Database,
      color: "purple",
      points: [
        "Bumppy may use the KYC submitted by you for business purposes.",
        "You consent to receiving e-newsletters and communications containing offers.",
        "Bumppy may provide information to partners for the purpose of providing relevant offers.",
        "You agree to indemnify Bumppy against any claims arising from misuse of shared data.",
      ],
    },
    {
      id: 3,
      title: "Cookies & Technology",
      icon: Cookie,
      color: "orange",
      content:
        "We collect technical information from your mobile device including location data, device characteristics, IP address, and interaction with technology such as NFC Tags or QR Codes. Your browser settings allow you to refuse cookies if you wish.",
    },
    {
      id: 4,
      title: "Data Security",
      icon: Lock,
      color: "emerald",
      points: [
        "We use reasonable endeavours to protect personal information from unauthorized access or misuse.",
        "Information is destroyed when no longer required under this policy.",
        "Users are responsible for keeping their passwords confidential.",
        "No guarantee can be provided for data security during internet transmission.",
      ],
    },
    {
      id: 5,
      title: "Data Usage",
      icon: Share2,
      color: "cyan",
      points: [
        "Processing and delivering orders placed on the Website.",
        "Disclosing contact details to riders/partners for delivery purposes.",
        "Advertising and direct marketing of Bumppy's goods and services.",
        "To unsubscribe from marketing, email: support@bumppy.com",
      ],
    },
    {
      id: 6,
      title: "Location Tracking",
      icon: MapPin,
      color: "pink",
      points: [
        "Sales Manager app collects location to find nearby bluetooth printers.",
        "Location can be accessed even when the app is closed to ensure service continuity.",
        "Sales Team app tracks trips to calculate distance travelled for work purposes.",
      ],
    },
    {
      id: 7,
      title: "Refund & Cancellation",
      icon: CreditCard,
      color: "green",
      subsections: [
        {
          subtitle: "Refund Conditions",
          items: [
            "Overpayment of a bill",
            "Service not rendered",
            "Payment made in error",
          ],
        },
        {
          subtitle: "Policy Details",
          items: [
            "7-day refund policy window",
            "5-7 business days for source account reversal",
            "No refunds for subscription services",
          ],
        },
      ],
    },
    {
      id: 8,
      title: "Your Data Rights",
      icon: UserCheck,
      color: "violet",
      content:
        "You can request an exported file of the personal data we hold about you. You can also request that we erase any personal data, excluding data we are obliged to keep for legal or security purposes.",
    },
    {
      id: 9,
      title: "Contact & Grievance Officer",
      icon: Mail,
      color: "emerald",
      subsections: [
        {
          subtitle: "Contact Information",
          items: [
            "If you have any questions, concerns, or requests regarding this Privacy Policy or the processing of your personal information, you may contact us using the details provided below.",
            "Company Name: BUMPPY PAYMENTS PRIVATE LIMITED",
            "Registered Office: B-218, Ithum Tower, Sector-62, Noida - 201301, Uttar Pradesh, India",
            "Website: https://payments.bumppy.com",
            "Email: support@bumppy.com",
          ],
        },
        {
          subtitle: "Grievance Officer",
          items: [
            "In accordance with the Information Technology Act, 2000 and the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, the Company has appointed a Grievance Officer to address complaints relating to the collection, processing, storage, use, and protection of personal information.",
            "Grievance Officer Name: Sumit Kasana",
            "Designation: Grievance Officer",
            "Email: sumit.kasana@bumppy.com",
            "Office Address: B-218, Ithum Tower, Sector-62, Noida - 201301, Uttar Pradesh, India",
            "The Company will acknowledge and make reasonable efforts to resolve all privacy-related grievances within the timelines prescribed under applicable laws.",
          ],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="relative bg-[#0F172A] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-indigo-600/10 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm font-medium mb-6 animate-fadeIn">
            <Shield size={16} />
            <span>Secure & Transparent</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-spacegrotesk font-bold text-white mb-6 tracking-tight animate-fadeIn">
            Privacy <span className="text-indigo-500">Policy</span>
          </h1>
          <p
            className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed animate-fadeIn"
            style={{ animationDelay: "200ms" }}
          >
            Your data is your asset. Learn how BUMPPY PAYMENTS PVT LTD protects,
            manages, and respects your personal information.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-20">
        <div className="flex flex-col lg:flex-row gap-12">
          <aside className="hidden lg:block w-72 h-fit sticky top-10">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 px-4">
                Policy Sections
              </p>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeSection === s.id
                        ? "bg-indigo-50 text-indigo-600 shadow-sm"
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    <s.icon size={18} />
                    <span className="font-semibold text-sm truncate">
                      {s.title}
                    </span>
                    {activeSection === s.id && (
                      <ChevronRight size={14} className="ml-auto" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="mt-6 p-6 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-2xl text-white shadow-lg shadow-indigo-100">
              <h4 className="font-bold mb-2">Privacy Concerns?</h4>
              <p className="text-xs text-indigo-100 mb-4 leading-relaxed">
                Contact our Data Protection Officer for any specific queries.
              </p>
              <a
                href="mailto:support@bumppy.com"
                className="text-xs font-bold bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition-colors block text-center uppercase tracking-wider"
              >
                Email Support
              </a>
            </div>
          </aside>

          <main className="flex-1 space-y-8">
            <div className="space-y-6">
              {sections.map((section) => (
                <div
                  key={section.id}
                  className={`bg-white rounded-3xl border transition-all duration-300 ${
                    activeSection === section.id
                      ? "border-indigo-200 shadow-xl shadow-indigo-50/50"
                      : "border-slate-200 shadow-sm"
                  }`}
                >
                  <button
                    onClick={() => setActiveSection(section.id)}
                    className="w-full text-left p-6 md:p-8 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-5">
                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${section.color}-50 text-${section.color}-600`}
                      >
                        <section.icon size={24} />
                      </div>
                      <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                        {section.id}. {section.title}
                      </h2>
                    </div>
                    <ChevronRight
                      className={`text-slate-400 transition-transform duration-300 ${activeSection === section.id ? "rotate-90" : ""}`}
                    />
                  </button>

                  {activeSection === section.id && (
                    <div className="px-6 pb-8 md:px-8 md:pb-10 animate-fadeIn">
                      <div className="h-px bg-slate-100 mb-8" />

                      {section.content && (
                        <p className="text-slate-600 leading-relaxed mb-6 text-lg">
                          {section.content}
                        </p>
                      )}

                      {section.points && (
                        <div className="grid gap-4">
                          {section.points.map((pt, i) => (
                            <div
                              key={i}
                              className="flex gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:border-indigo-200 transition-all"
                            >
                              <div className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                              <span className="text-slate-700 font-medium text-sm md:text-base">
                                {pt}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {section.subsections && (
                        <div className="space-y-8 mt-4">
                          {section.subsections.map((sub, i) => (
                            <div
                              key={i}
                              className="bg-slate-50 rounded-2xl p-6 border border-slate-100"
                            >
                              <h4 className="text-slate-900 font-bold mb-4 flex items-center gap-2">
                                <div className="w-1 h-5 bg-green-500 rounded-full" />
                                {sub.subtitle}
                              </h4>
                              <ul className="space-y-3">
                                {sub.items.map((item, idx) => (
                                  <li
                                    key={idx}
                                    className="flex items-start gap-3 text-slate-600 text-sm"
                                  >
                                    <ChevronRight
                                      size={14}
                                      className="mt-1 text-green-500"
                                    />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <div className="p-8 rounded-3xl bg-indigo-50 border border-indigo-100">
                <h3 className="text-lg font-bold text-indigo-900 mb-2">
                  Other Websites
                </h3>
                <p className="text-indigo-800/70 text-sm leading-relaxed">
                  Our Website may have links to other sites. This privacy policy
                  only applies to this Website. Please read others' policies
                  separately.
                </p>
              </div>
              <div className="p-8 rounded-3xl bg-amber-50 border border-amber-100">
                <h3 className="text-lg font-bold text-amber-900 mb-2">
                  Policy Changes
                </h3>
                <p className="text-amber-800/70 text-sm leading-relaxed">
                  Bumppy reserves the right to alter this policy. Changes will
                  be notified via Website or e-mail.
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>

      <CtaSection />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        
        /* Color Helper Classes */
        .bg-blue-50 { background-color: #eff6ff; } .text-blue-600 { color: #2563eb; }
        .bg-purple-50 { background-color: #faf5ff; } .text-purple-600 { color: #9333ea; }
        .bg-orange-50 { background-color: #fff7ed; } .text-orange-600 { color: #ea580c; }
        .bg-emerald-50 { background-color: #ecfdf5; } .text-emerald-600 { color: #059669; }
        .bg-cyan-50 { background-color: #ecfeff; } .text-cyan-600 { color: #0891b2; }
        .bg-pink-50 { background-color: #fdf2f8; } .text-pink-600 { color: #db2777; }
        .bg-green-50 { background-color: #f0fdf4; } .text-green-600 { color: #16a34a; }
        .bg-violet-50 { background-color: #f5f3ff; } .text-violet-600 { color: #7c3aed; }
      `}</style>
    </div>
  );
};

export default PrivacyPolicyPage;
